<script setup>
import { ref } from 'vue';

const props = defineProps({
    recommendations: {
        type: Array,
        required: false,
        default: () => [],
    },
    talkingPoints: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const showRecommendations = ref(true);
const showTalkingPoints = ref(true);

const priorityClasses = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-green-100 text-green-700',
};

const priorityLabels = {
    high: () => _mft('crm:nina.priority.high'),
    medium: () => _mft('crm:nina.priority.medium'),
    low: () => _mft('crm:nina.priority.low'),
};
</script>

<template>
    <div class="mt-3 space-y-3">
        <!-- Recommendations -->
        <div v-if="recommendations.length > 0" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
                class="flex items-center justify-between w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                @click="showRecommendations = !showRecommendations"
            >
                <span class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5Z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                    </svg>
                    <mf-translate domainkey="crm:nina.recommendations"></mf-translate>
                </span>
                <svg
                    class="w-4 h-4 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': showRecommendations }"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <div v-if="showRecommendations" class="px-4 pb-3 space-y-2">
                <div
                    v-for="(rec, i) in recommendations"
                    :key="i"
                    class="p-3 bg-gray-50 rounded-lg"
                >
                    <div class="flex items-center gap-2 mb-1">
                        <span
                            class="px-2 py-0.5 text-xs font-medium rounded-full"
                            :class="priorityClasses[rec.priority] || priorityClasses.medium"
                        >
                            {{ (priorityLabels[rec.priority] || priorityLabels.medium)() }}
                        </span>
                        <span class="text-sm font-medium text-gray-800">{{ rec.title }}</span>
                    </div>
                    <p class="text-sm text-gray-600">{{ rec.description }}</p>
                </div>
            </div>
        </div>

        <!-- Talking Points -->
        <div v-if="talkingPoints.length > 0" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
                class="flex items-center justify-between w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                @click="showTalkingPoints = !showTalkingPoints"
            >
                <span class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <mf-translate domainkey="crm:nina.talkingPoints"></mf-translate>
                </span>
                <svg
                    class="w-4 h-4 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': showTalkingPoints }"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <div v-if="showTalkingPoints" class="px-4 pb-3">
                <ul class="space-y-1.5">
                    <li
                        v-for="(point, i) in talkingPoints"
                        :key="i"
                        class="flex items-start gap-2 text-sm text-gray-600"
                    >
                        <span class="flex-shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-aiAgentSalesAssistant"></span>
                        {{ point }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
